/**
 * PreCompareConfirmation — the confirmation box in front of every Compare run
 * (fu4 sub-task 13, Trello #748 second half).
 *
 * Clicking "Compare to Thrive" used to fire the run immediately, selection-
 * aware but silently. This modal states exactly what is about to run —
 * "Compare the N selected contacts" vs "Compare all X contacts" — lets the
 * person switch from the selection to everyone, shows a coarse time estimate
 * (`compareEstimate.ts`), and surfaces the partial-coverage facts
 * (never-compared / stale-compared counts from `counts.review`, sub-task 10's
 * wire contract).
 *
 * The estimate is a function of list size AND composition, not size alone —
 * see `fallbackShare` below, which reads the same `counts.review` block the
 * coverage nudge already fetches, so the second term costs no extra request.
 *
 * The scope numbers arrive from the page's `computeCompareScope` results, read
 * through `comparePendingCount` — the SAME source the run scope uses — so the
 * modal can never display a count the enqueued run won't honour (pinned by the
 * interposition test's spy).
 *
 * Coverage counts are fetched on mount; the component only mounts while open,
 * so every open sees fresh numbers. The fetch failing (or the counts block
 * missing) just hides the nudge — it must never block the run.
 *
 * Cancel and Escape have ZERO side effects: nothing is enqueued, no state
 * beyond closing survives.
 */

import { Button } from "@nu-assist/ui";
import { useEffect, useState } from "react";

import type { ImportsClient } from "../../clients/imports.client";
import { formatCompareEstimate } from "./compareEstimate";
import {
  type CompareScopeBreakdown,
  MAX_EXPLICIT_COMPARE_SELECTION,
  compareScopeFromReviewCounts,
} from "./computeCompareScope";
import {
  PRE_COMPARE_ALL_UNKNOWN_LABEL,
  PRE_COMPARE_CANCEL,
  PRE_COMPARE_COVERAGE_SEPARATOR,
  PRE_COMPARE_ESTIMATE_FOOTNOTE,
  PRE_COMPARE_QUESTION,
  PRE_COMPARE_RUN,
  PRE_COMPARE_STATEMENT,
  PRE_COMPARE_TITLE,
  preCompareAllLabel,
  preCompareEverythingLabel,
  preCompareNeverComparedLine,
  preCompareSelectedLabel,
  preCompareSkippedLine,
  preCompareStaleLine,
  preCompareTooManySelected,
} from "./preCompareCopy";
import { type ScopeChoice, resolveScopeChoice } from "./scopeChoice";

/** Compare's name for the shared `ScopeChoice`. Re-exported rather than
 *  restated so the two can never drift apart. */
export type PreCompareChoice = ScopeChoice;

interface ReviewCoverage {
  notCompared: number;
  staleCompared: number;
  notInThrive: number;
  exported: number;
  ignored: number;
}

export function PreCompareConfirmation({
  client,
  importId,
  selectedScope,
  allScope,
  allTotal,
  onRun,
  onCancel,
}: {
  client: ImportsClient;
  importId: string;
  /** The current selection's scope breakdown from `computeCompareScope` —
   *  exact on every field, because that branch filters an id list. `null` = no
   *  active selection → single "all" statement, no radio group. */
  selectedScope: CompareScopeBreakdown | null;
  /** The whole-import arm as the PAGE can compute it from the export aggregate:
   *  already net of the not-yet-exported narrowing the run applies, but blind
   *  to dismissals (there is no whole-import dismissal aggregate). Shown until
   *  the counts fetch below lands, then replaced by the exact breakdown. */
  allScope: CompareScopeBreakdown;
  /** The import's contact total — what the exact whole-import breakdown
   *  subtracts the unfiltered `counts.review` exclusions FROM. */
  allTotal: number;
  onRun: (choice: PreCompareChoice) => void;
  onCancel: () => void;
}) {
  const hasSelection = selectedScope !== null;
  const [choice, setChoice] = useState<PreCompareChoice>(hasSelection ? "selection" : "all");
  const [coverage, setCoverage] = useState<ReviewCoverage | null>(null);

  // Refetch-on-open: the component mounts only while the confirmation is open,
  // so a mount-time fetch is by construction a fresh read. `q: undefined`
  // rides along like the table query's filter keys do (`buildQuery` drops it
  // from the wire) — the unfiltered whole-import counts are what the nudge
  // describes. Failure or a missing counts block hides the nudge, nothing else.
  useEffect(() => {
    let cancelled = false;
    client.listContacts(importId, { page: 1, page_size: 1, q: undefined }).then(
      (res) => {
        const review = res.counts?.review;
        if (cancelled || !review) return;
        setCoverage({
          notCompared: review.not_compared,
          staleCompared: review.stale_compared,
          notInThrive: review.not_in_thrive,
          exported: review.exported,
          ignored: review.ignored,
        });
      },
      () => {
        // Coverage is a nudge, never a gate — a failed fetch just hides it.
      }
    );
    return () => {
      cancelled = true;
    };
  }, [client, importId]);

  // Escape = Cancel, with the same zero side effects.
  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") onCancel();
    }
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [onCancel]);

  /**
   * How many of `n` contacts will miss Compare's batched path and pay a
   * throttled fallback probe — the term that dominates the time estimate.
   *
   * A contact rides the fallback when the chunked `in` query does not return
   * its canonical LinkedIn URL. Nothing on the wire says that directly, so the
   * proxy is the prior verdict, and it is a good one on both arms:
   *
   *  - `not_in_thrive` — Compare already looked and found nothing. Thrive will
   *    not return that URL this time either, so the row pays the probe AGAIN.
   *    Leaving it out is exactly the systematic under-estimate the calibration
   *    script warns about, on the commonest re-compare shape there is.
   *  - `not_compared` — nobody has looked. Unknowable; counted as fallback
   *    because a freshly-sourced list is mostly not in Thrive yet, and because
   *    over-promising time is the safe direction for a promise.
   *
   * Every other status implies a row Compare matched by URL, which is the
   * batched path. (The W3 rows inside `to_review` matched by NAME, so they pay
   * a probe too and are not counted here — a small under-estimate that only
   * shows up on W3-heavy lists.)
   *
   * With the counts still loading or their fetch failed, every contact is
   * assumed to ride the fallback: the estimate then reads as the ceiling
   * rather than the floor, and correcting DOWN when the numbers land is the
   * harmless direction.
   */
  function fallbackShare(n: number): number {
    if (!coverage) return n;
    return Math.min(n, coverage.notCompared + coverage.notInThrive);
  }

  const coverageLines: string[] = [];
  if (coverage && coverage.notCompared > 0) {
    coverageLines.push(preCompareNeverComparedLine(coverage.notCompared));
  }
  if (coverage && coverage.staleCompared > 0) {
    coverageLines.push(preCompareStaleLine(coverage.staleCompared));
  }

  // The whole-import arm, exact ONLY once the unfiltered counts land: the run
  // drops already-exported rows (client-side narrowing) AND dismissed ones (the
  // server's scope query), and `counts.review` is the only place both are
  // countable over the whole import.
  //
  // Until then the page's aggregate-derived `allScope` stands in — but its
  // number is NOT shown, because it is blind to dismissals (`ignoredCount: 0`
  // on that branch of `computeCompareScope`) and would therefore over-state
  // what the run compares. It still feeds the ESTIMATE, where an upper bound is
  // the safe direction. See `PRE_COMPARE_ALL_UNKNOWN_LABEL`.
  const all: CompareScopeBreakdown = coverage
    ? compareScopeFromReviewCounts(allTotal, coverage)
    : allScope;
  const selectedSkipped = selectedScope
    ? preCompareSkippedLine(selectedScope.alreadyExported, selectedScope.ignored)
    : null;
  // Suppressed with the counts unavailable: the exported half would be true and
  // the dismissed half unknown, which reads as "and nothing else was skipped".
  const allSkipped = coverage ? preCompareSkippedLine(all.alreadyExported, all.ignored) : null;
  const selectedCount = selectedScope?.pending ?? 0;
  const allCount = all.pending;
  /**
   * TRUE when the two arms would enqueue the SAME ROWS, so there is nothing to
   * choose between them and the modal states instead of asking.
   *
   * Equal counts imply equal SETS here, and that is a proof rather than a
   * coincidence worth checking: `selectedCount` is `|selection ∩ pending|` and
   * `allCount` is `|pending|`, so the two are equal exactly when the selection
   * CONTAINS the pending set — and then the intersection IS the pending set.
   * There is no shape where the numbers agree and the rows differ.
   *
   * Gated on `coverage` because `allCount` is only exact once the unfiltered
   * counts land: until then it comes from the page's aggregate, which is blind
   * to dismissals and OVER-states. Collapsing on that number could equate a
   * 15-row selection with a 12-row run.
   */
  // THE RULES NOW LIVE IN `scopeChoice.ts`, unchanged, so Add to Thrive's own
  // dialog inherits them instead of re-deriving the collapse and the over-cap
  // override. The reasoning that produced each one is in that file; what stays
  // here is Compare's own vocabulary for the inputs.
  //
  // `countsAreExact` is `coverage !== null`: until the unfiltered counts land,
  // `allCount` comes from the page's aggregate, which is blind to dismissals
  // and OVER-states.
  const { choiceIsNotAChoice, effectiveChoice, selectionTooLarge } = resolveScopeChoice({
    // Compare's two numbers ARE the same predicate over the same universe:
    // both count pending rows, one of them intersected with the selection.
    // So equal counts really do imply equal sets here and the collapse is
    // sound. Add cannot say this, which is why the flag exists.
    countsAreComparable: true,
    hasSelection,
    countsAreExact: coverage !== null,
    selectedCount,
    // Compare's promise and its payload are the SAME set — this arm filters
    // the id list it sends — so the two counts coincide here. Add's do not,
    // which is the whole reason the parameter exists.
    selectedPayloadCount: selectedCount,
    allCount,
    cap: MAX_EXPLICIT_COMPARE_SELECTION,
    choice,
  });
  const tooManyNoteId = "pre-compare-too-many-note";
  // The selection arm has no such problem — it filters an id list, so its count
  // IS the enqueued list. Only the whole-import arm goes numberless.
  const allLabel = coverage ? preCompareAllLabel(allCount) : PRE_COMPARE_ALL_UNKNOWN_LABEL;

  return (
    <div
      // biome-ignore lint/a11y/useSemanticElements: same conditionally-rendered
      // overlay pattern as CompareToThriveModal — role="dialog" + aria-modal +
      // aria-labelledby is the accessible fit here.
      role="dialog"
      aria-modal="true"
      aria-labelledby="pre-compare-title"
      data-testid="pre-compare-modal"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
    >
      <div className="flex w-full max-w-md flex-col rounded-lg border border-border bg-surface shadow-lg">
        <div className="p-5">
          <h2 id="pre-compare-title" className="text-h1 text-text">
            {PRE_COMPARE_TITLE}
          </h2>
          <p className="mt-1 text-body text-text-secondary">
            {choiceIsNotAChoice ? PRE_COMPARE_STATEMENT : PRE_COMPARE_QUESTION}
          </p>

          {choiceIsNotAChoice ? (
            // ONE statement, one click. The radios are gone rather than
            // pre-selected: a radio group of one still asks to be read, and
            // the recruiter who reads it finds the same number twice.
            <div
              data-testid="pre-compare-scope-everything"
              className="mt-4 flex items-center justify-between gap-3 rounded-md border border-border p-3 text-body text-text"
            >
              <span className="flex flex-col">
                {preCompareEverythingLabel(selectedCount)}
                {selectedSkipped && (
                  <span
                    data-testid="pre-compare-skipped-selected"
                    className="text-caption text-text-secondary"
                  >
                    {selectedSkipped}
                  </span>
                )}
              </span>
              <span className="text-caption text-text-secondary">
                {formatCompareEstimate(selectedCount, fallbackShare(selectedCount))}
              </span>
            </div>
          ) : hasSelection ? (
            <div
              role="radiogroup"
              aria-label={PRE_COMPARE_QUESTION}
              className="mt-4 flex flex-col gap-2"
            >
              <label className="flex cursor-pointer items-center justify-between gap-3 rounded-md border border-border p-3 text-body text-text">
                <span className="flex items-center gap-2.5">
                  <input
                    type="radio"
                    name="pre-compare-scope"
                    data-testid="pre-compare-scope-selected"
                    className="accent-accent"
                    checked={choice === "selection"}
                    onChange={() => setChoice("selection")}
                  />
                  <span className="flex flex-col">
                    {preCompareSelectedLabel(selectedCount)}
                    {selectedSkipped && (
                      <span
                        data-testid="pre-compare-skipped-selected"
                        className="text-caption text-text-secondary"
                      >
                        {selectedSkipped}
                      </span>
                    )}
                  </span>
                </span>
                <span className="text-caption text-text-secondary">
                  {formatCompareEstimate(selectedCount, fallbackShare(selectedCount))}
                </span>
              </label>
              <label className="flex cursor-pointer items-center justify-between gap-3 rounded-md border border-border p-3 text-body text-text">
                <span className="flex items-center gap-2.5">
                  <input
                    type="radio"
                    name="pre-compare-scope"
                    data-testid="pre-compare-scope-all"
                    className="accent-accent"
                    checked={choice === "all"}
                    onChange={() => setChoice("all")}
                  />
                  <span className="flex flex-col">
                    {allLabel}
                    {allSkipped && (
                      <span
                        data-testid="pre-compare-skipped-all"
                        className="text-caption text-text-secondary"
                      >
                        {allSkipped}
                      </span>
                    )}
                  </span>
                </span>
                <span className="text-caption text-text-secondary">
                  {formatCompareEstimate(allCount, fallbackShare(allCount))}
                </span>
              </label>
            </div>
          ) : (
            <div
              data-testid="pre-compare-scope-all"
              className="mt-4 flex items-center justify-between gap-3 rounded-md border border-border p-3 text-body text-text"
            >
              <span className="flex flex-col">
                {allLabel}
                {allSkipped && (
                  <span
                    data-testid="pre-compare-skipped-all"
                    className="text-caption text-text-secondary"
                  >
                    {allSkipped}
                  </span>
                )}
              </span>
              <span className="text-caption text-text-secondary">
                {formatCompareEstimate(allCount, fallbackShare(allCount))}
              </span>
            </div>
          )}

          {coverageLines.length > 0 && (
            <p
              data-testid="pre-compare-coverage-note"
              className="mt-4 text-caption text-text-secondary"
            >
              {coverageLines.join(PRE_COMPARE_COVERAGE_SEPARATOR)}
            </p>
          )}

          <p className="mt-4 text-caption text-text-muted">{PRE_COMPARE_ESTIMATE_FOOTNOTE}</p>
        </div>

        {selectionTooLarge && (
          <p
            id={tooManyNoteId}
            data-testid="pre-compare-too-many"
            className="border-t border-border px-4 pt-3 text-body text-warning-ink"
          >
            {preCompareTooManySelected(selectedCount, MAX_EXPLICIT_COMPARE_SELECTION)}
          </p>
        )}

        <div className="flex items-center justify-end gap-2 border-t border-border p-4">
          <Button
            variant="secondary"
            type="button"
            data-testid="pre-compare-cancel"
            onClick={onCancel}
          >
            {PRE_COMPARE_CANCEL}
          </Button>
          <Button
            type="button"
            data-testid="pre-compare-run"
            // The collapsed case runs the SELECTION arm — EXCEPT when that
            // selection is over the cap, where it runs `all` instead (see
            // `effectiveChoice`). This comment used to state the rule without
            // the exception, which by then was the opposite of what the line
            // below did, and read as an instruction to re-create the dead end
            // iteration 4 found. Caught by two reviewers in iteration 5.
            //
            // The exception costs something real and it is worth naming: the
            // selection arm pins the rows at modal-open by sending explicit
            // ids, while `all` re-derives them server-side at enqueue. So on
            // the over-cap path a row added between opening this dialog and
            // clicking Run WILL be included. That is the trade for the run
            // happening at all — the alternative was a disabled button beside
            // copy pointing at a control the collapsed layout does not render.
            //
            // `aria-disabled`, NOT `disabled` — the repo's own rule, written
            // down at `ImportPicker.tsx` and tested there: the `disabled`
            // attribute drops the control out of the tab order and takes the
            // explanation with it, from exactly the users it is for. The button
            // stays focusable, announces the reason through `aria-describedby`,
            // and the handler guards instead.
            aria-disabled={selectionTooLarge || undefined}
            aria-describedby={selectionTooLarge ? tooManyNoteId : undefined}
            // `opacity-40` + `cursor-not-allowed`, and NOT a hover override.
            //
            // An earlier version of this added `hover:bg-accent` to cancel the
            // primary variant's `hover:bg-accent-hover`. It did nothing:
            // `Button` concatenates `className` after the variant, but class
            // ORDER IN THE ATTRIBUTE does not resolve a CSS conflict —
            // stylesheet order does, specificity being equal, and the variant's
            // rule is emitted later. The comment claiming it fixed the hover
            // tint was wrong for a whole review round. Caught by the closing
            // pass, which is what that pass is for.
            //
            // The precedent that DOES suppress the tint (`ImportPicker`) works
            // by OMITTING the hover utility — available to it because it
            // renders a bare `<button>`, not `Button`, whose variant always
            // carries one.
            //
            // (An earlier version of this comment counted the repo's
            // `aria-disabled` sites and got the number wrong — it said three;
            // `grep -rln aria-disabled packages/web/src --include='*.tsx'`
            // excluding tests returns five files. Counted this time. The
            // sentence no longer depends on the count, which is the better
            // fix: a number in a comment rots, and this one rotted inside the
            // commit whose whole subject was comments that say untrue things.)
            //
            // So the blocked button still brightens on hover. `opacity-40` and
            // the not-allowed cursor carry the affordance, the click is inert,
            // and the reason is announced. Doing better means giving `Button`
            // real `aria-disabled` support — recorded as a PRD follow-up rather
            // than worked around here with an `!important` this codebase does
            // not otherwise use.
            className={selectionTooLarge ? "opacity-40 cursor-not-allowed" : undefined}
            onClick={() => {
              if (selectionTooLarge) return;
              onRun(effectiveChoice);
            }}
          >
            {PRE_COMPARE_RUN}
          </Button>
        </div>
      </div>
    </div>
  );
}
